import { useEffect, useRef, useState } from 'react';
import { sfx } from '../audio/sfx';
import { money } from './format';
import { cx } from './cx';
import s from './ui.module.scss';

/**
 * Une cagnotte qui defile jusqu'a sa nouvelle valeur.
 *
 * Un nombre qui saute de 1200 a 1640 ne dit pas s'il a monte ou baisse ; le
 * meme nombre qui DEFILE le dit sans un mot, et laisse le temps de le
 * ressentir. Dans un jeu ou tout tient a la cagnotte, c'est le retour le plus
 * important de l'ecran.
 *
 * L'animation dure un temps fixe quelle que soit la distance : une grosse
 * somme defile plus vite, elle ne defile pas plus longtemps. Sinon un tapis
 * gagnant bloquerait l'ecran pendant que la manche suivante commence.
 */
export interface MoneyProps {
  value: number;
  className?: string | undefined;
  /**
   * Joue le son de gain/perte. Faux par defaut : ce composant affiche TOUTE
   * cagnotte, y compris celle des autres joueurs. Un encaissement general
   * (le revenu aux des) ferait sonner huit pieces a la fois si chaque ligne
   * du classement parlait — seule TA propre ligne doit se faire entendre.
   */
  sound?: boolean;
}

const ROLL_MS = 600;

type Direction = 'flat' | 'up' | 'down';

export function Money({ value, className, sound = false }: MoneyProps) {
  const [shown, setShown] = useState(value);
  const [direction, setDirection] = useState<Direction>('flat');
  /** La valeur d'ou part l'animation en cours. */
  const fromRef = useRef(value);
  const frameRef = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    if (from === value) return;
    const rising = value > from;
    setDirection(rising ? 'up' : 'down');
    if (sound) (rising ? sfx.chipGain : sfx.chipLoss)();

    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ROLL_MS);
      // Amorti en sortie : le compteur ralentit en arrivant, ce qui rend la
      // valeur finale lisible au lieu de la faire claquer.
      const eased = 1 - (1 - t) ** 3;
      setShown(Math.round(from + (value - from) * eased));
      if (t < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = value;
      }
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [value, sound]);

  return (
    <span
      className={cx(
        s.money,
        direction === 'up' && s.moneyUp,
        direction === 'down' && s.moneyDown,
        className,
      )}
    >
      {money(shown)}
    </span>
  );
}
